import React, { useCallback, useState, Fragment, useMemo } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { v4 } from 'uuid';
import { useAsyncEffect } from 'ahooks';
import { setTempState, useTempState } from '@starflow/app/store';
import { BASETYPE_NAME_TO_VALUE, BASETYPE_VALUE_TO_SHORT_LABEL } from '@shakkerai/pro-models';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { NumberInputGroup } from '@shakkerai/pro-components';
import { LoraDetailWindow, presetCheckpointsMap } from '@starflow/app/modules/models';
import { SuggestionBaseModelTipWrapper, NewlyFavoritedModelTipWrapper, ModelAddedTip, NormalGide } from './tips';
import crownIcon from './assets/crown_outlined.png';
import imgLoraEntry from './assets/lora-entry.svg';
import imgBgPresetModelCard from './assets/bg_preset_modal_card.png';
import { libVipIcon } from '@shakkerai/pro-commercial';
import { isMainland } from '@shakkerai/pro-utils';
import loadingImg from './assets/loading.png';
import { request } from '@shakkerai/services';
import styles from './index.module.scss';
import cs from 'classnames';
import { switchGlobalMode } from '../../helpers/generate';

export const PresetModelCard = (props) => {
  const presetData = presetCheckpointsMap[props.id] || presetCheckpointsMap['base_checkpoint'];
  const isBaseModel = presetData.id === 'base_checkpoint';

  return (
    <div className="relative w-full h-[50px] overflow-hidden bg-[#727476] rounded">
      <img src={presetData.imageUrl} className="absolute inset-0 w-full h-[50px] object-cover" />
      <div className={`absolute inset-0 flex items-center pr-4 ${isBaseModel && isMainland ? '' : 'bg-black/70'}`}>
        <img
          src={isBaseModel ? presetData.imageIcon : presetData.imageUrl}
          className="w-[50px] h-[50px] object-cover rounded"
        />
        <span className="flex-1 text-xs font-semibold text-white ml-2">{presetData.displayName}</span>
      </div>
      {props.ckptLoading ? (
        <img
          className={`w-[30px] h-[30px] object-cover mr-[8px] absolute left-[10px] top-[10px] ${styles['animate-spin-ckpt']}`}
          src={loadingImg}
          alt=""
        />
      ) : null}
    </div>
  );
};

export const CustomModelCard = ({ data, isSimpleMode, readonly = false }) => {
  const coverUrl = data.imageUrl ? convert2CompressedUrl(data.imageUrl, 120) : imgBgPresetModelCard;

  const baseTypeLabel =
    BASETYPE_VALUE_TO_SHORT_LABEL[data.baseType] ||
    BASETYPE_VALUE_TO_SHORT_LABEL[BASETYPE_NAME_TO_VALUE[data.baseType]] ||
    data.baseType;

  return (
    <div className="relative w-full h-[50px] overflow-hidden bg-[#727476] rounded">
      <img src={coverUrl} className="absolute w-full h-[50px] object-cover" />
      <div className="absolute inset-0 flex items-center pr-4 bg-black/70">
        <img src={coverUrl} className="w-[50px] h-[50px] object-cover rounded" />
        <div className="flex flex-col flex-1 mr-2 ml-2 overflow-hidden">
          <span className="self-stretch text-xs font-semibold whitespace-nowrap text-ellipsis overflow-hidden text-white">
            {data.absName || data.name}
          </span>
          {!isSimpleMode && (
            <div className="flex gap-2 mt-1 items-center text-xs text-[#FFFFFF99] whitespace-nowrap">
              {data.vipUsed === 1 && (
                <>
                  <img
                    src={isMainland ? libVipIcon : crownIcon}
                    className={`w-[13px] relative ${isMainland ? '' : '-top-[1.5px]'}`}
                    alt=""
                  />
                  <div className="bg-[#FFFFFF99] w-[1px] h-[10px]"></div>
                </>
              )}
              <span>{baseTypeLabel}</span>
              {data.versionName && (
                <>
                  <div className="bg-[#FFFFFF99] w-[1px] h-[10px]"></div>
                  <span className="text-xs text-[#FFFFFF99] w-0 flex-grow overflow-hidden">{data.versionName}</span>
                </>
              )}
            </div>
          )}
        </div>
        {!readonly && <Icon className="shk-danchuang-gengduomoxing1" fontSize={12} color="#fff" />}
      </div>
    </div>
  );
};

export const Models = ({ generator }) => {
  const globalMode = useTempState('globalMode');
  const showNormalCkptGide = useTempState('showNormalCkptGide');
  const showLoraAddedTip = useTempState('showLoraAddedTip');
  const showCheckpointAddedTip = useTempState('showCheckpointAddedTip');
  const showSuggestionBaseModelTip = useTempState('suggestionCheckpoint');
  const newlyFavoritedModel = useTempState('newlyFavoritedModel');
  const { checkpoint, loraModels, presetBaseModelId, baseType } = generator.data;
  const [openedLoraData, setOpenedLoraData] = useState(null);
  const openedSubWindow = useTempState('exclusiveWindow');
  const [ckptLoading, setCkptLoading] = useState(false);
  const [hasLikedModel, setHasLikedModel] = useState(false);

  const loraDetailOpened = openedSubWindow === 'lora-detail';
  const isPrimaryGenerator = generator.type === 'generator';
  const isSimpleMode = globalMode === 'simple';

  const handleCloseModelAddedTip = useCallback(() => {
    setTempState({ showLoraAddedTip: false, showCheckpointAddedTip: false });
  }, []);

  const baseTypeLabel = BASETYPE_VALUE_TO_SHORT_LABEL[baseType] || baseType;

  const handleOpenLoraDetail = useCallback((loraData) => {
    setOpenedLoraData(loraData);
    setTempState({ exclusiveWindow: 'lora-detail' });
  }, []);

  useAsyncEffect(async () => {
    if (globalMode === 'pro') return;
    setCkptLoading(true);
    try {
      // 用于判断用户是否有收藏
      const result = await request('/api/www/model/tool/like/list', {
        modelType: '1',
        page: 1,
        pageSize: 30,
        requestId: v4(),
      });
      setHasLikedModel(result.data.length > 0);
    } catch (err) {
      console.log('api error', err);
    } finally {
      setCkptLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hanldeOpenCheckpointsWindow = useCallback(
    (event) => {
      if (globalMode !== 'pro') {
        // if (hasLikedModel) {
        //   switchGlobalMode('pro');
        //   setTempState({ exclusiveWindow: 'model-checkpoints' });
        // } else {
        //   setTempState({ showNormalCkptGide: true, imagesWindowOpened: false });
        // }
        setTempState({ showNormalCkptGide: true, imagesWindowOpened: false, exclusiveWindow: null });
      } else {
        // 直接呈现有收藏+高级的页面 || 无收藏+高级
        setTempState({ exclusiveWindow: 'model-checkpoints' });
      }

      event.stopPropagation();
      event.preventDefault();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [globalMode, hasLikedModel],
  );

  const hanldeOpenLorasWindow = useCallback((loraToReplace = null) => {
    handleCloseModelAddedTip();
    setTempState({ loraToReplace });
    setTempState({ exclusiveWindow: 'model-loras' });
    setTempState({ showNormalCkptGide: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveHiddenLoras = useCallback(() => {
    const { loraModels } = generator.data;

    updateUnitData(generator.id, {
      styleIcon: null,
      loraModels: loraModels.filter((lora) => !lora.isHiddenStrategy),
    });
  }, [generator]);

  const handleUnlinkLora = useCallback(
    (e) => {
      e.stopPropagation();
      handleCloseModelAddedTip();
      updateUnitData(generator.id, {
        loraModels: loraModels?.filter((i) => i?.versionId !== +e.currentTarget.dataset.id) || [],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loraModels, generator.id],
  );

  const handleWeight = useCallback(
    (weight, versionId) => {
      const { loraModels } = generator.data;

      updateUnitData(generator.id, {
        loraModels: loraModels.map((i) => {
          if (i?.versionId === versionId) {
            return { ...i, weight };
          }
          return i;
        }),
      });
    },
    [generator],
  );

  const isLoraDisabled = (lora) => {
    if (checkpoint) {
      return BASETYPE_NAME_TO_VALUE[lora.baseType] !== BASETYPE_NAME_TO_VALUE[checkpoint.baseType];
    }

    if (presetBaseModelId) {
      return BASETYPE_NAME_TO_VALUE[lora.baseType] !== baseType;
    }

    return true;
  };

  const modelEntry = (
    <div
      data-trigger-window="model-checkpoints"
      id="checkpointModelEntry"
      onClick={hanldeOpenCheckpointsWindow}
      className={`relative flex items-center w-[268px] h-[50px] mt-2.5 rounded overflow-hidden shrink-0 group cursor-pointer`}
    >
      {!checkpoint ? (
        <PresetModelCard id={presetBaseModelId} ckptLoading={ckptLoading} />
      ) : (
        <CustomModelCard data={checkpoint} isSimpleMode={isSimpleMode} />
      )}
    </div>
  );

  const loraTitle = (
    <div id="generator-lora-models-title" className="flex items-center w-full">
      <h5 id="model-loras-entry" className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
        {translate('model_title_lora')}
        <span className="text-[#b2b2b2]">&ensp;LoRA</span>
      </h5>
      <button
        data-trigger-window="model-loras"
        onClick={() => hanldeOpenLorasWindow()}
        className="flex items-center justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
      >
        <Icon className="icon-prozhankai" fontSize={12} color="#666" />
      </button>
    </div>
  );

  const renderLoraEntry = (lora) => {
    if (!lora) {
      return null;
    }

    return (
      <div
        key={lora?.versionId}
        id={`lora-detail-entry-${lora?.versionId}`}
        className="relative mt-2.5 flex gap-3 items-center justify-between"
      >
        <div className="relative h-[66px] w-[50px] rounded overflow-hidden group">
          <img className="h-full w-full object-cover" src={convert2CompressedUrl(lora.imageUrl, 100)} />
          {!!lora.versionName && (
            <span className="absolute bottom-0 right-0 left-0 h-4 bg-black/40 backdrop-blur text-center text-xs text-white">
              {lora.versionName}
            </span>
          )}
          <div
            data-trigger-window="model-loras"
            onClick={() => hanldeOpenLorasWindow(lora)}
            className="absolute left-0 top-0 h-full w-full flex items-center justify-center bg-black/65 hidden group-hover:flex cursor-pointer"
          >
            <Icon className={cs('icon-border_change', styles.bgHover)} size={12} color="#fff" />
          </div>
          <button
            data-trigger-window="lora-detail"
            onClick={() => handleOpenLoraDetail(lora)}
            className="absolute z-1 top-0 right-0 flex items-center justify-center w-[18px] h-[18px]"
          >
            <Icon className={cs('icon-border_more rotate-90', styles.bgHover)} fontSize={14} color="#fff" />
          </button>
        </div>
        <div className="flex-grow relative">
          <div className="flex item-center justify-between mt-1 overflow-hidden">
            <span className="w-[200px] mr-2 text-[#383838] font-grotesk font-semibold whitespace-nowrap text-ellipsis overflow-hidden text-[12px]">
              {lora.name}
            </span>
          </div>
          {!isSimpleMode && (
            <div className="flex items-center mt-0.5 gap-2">
              <span className="text-xs font-grotesk font-semibold text-[#7A7A7A]">
                {`${BASETYPE_VALUE_TO_SHORT_LABEL[BASETYPE_NAME_TO_VALUE[lora.baseType]]}`.replace(/\s/g, '')}
              </span>

              {lora.vipUsed === 1 && (
                <>
                  <div className="bg-[#EFEFEF] w-[1px] h-[10px]"></div>
                  <img src={isMainland ? libVipIcon : crownIcon} className="w-[13px] relative -top-[1.5px]" alt="" />
                </>
              )}
            </div>
          )}
          <div className="flex items-center justify-between h-5 mt-0.5">
            {isLoraDisabled(lora) ? (
              <div className="flex flex-1 items-center text-[#FF6565]">
                <Icon class="icon-border_rejected -rotate-45 mr-0.5" fontSize={10} color="inherit" />
                <div className="text-xs font-grotesk font-semibold leading-3">
                  {translate(`model_lora_unavailable_toast`, {
                    name: `${baseTypeLabel}`.replace(/\s/g, ''),
                  })}
                </div>
              </div>
            ) : (
              <>
                <span className="w-[54px] text-xs font-semibold text-[#7f7f7f]">{translate('image_info_weight')}</span>
                <NumberInputGroup
                  min={-2}
                  max={2}
                  value={lora.weight}
                  name={lora?.versionId}
                  onChange={handleWeight}
                  clampBehavior="strict"
                  step={0.1}
                  inputWidth={32}
                  allowNegative
                  allowDecimal
                  decimalScale={1}
                  hideControls
                />
              </>
            )}
            <button
              data-id={lora?.versionId}
              onClick={handleUnlinkLora}
              className="flex items-center ml-2 justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
            >
              <Icon className="icon-proshouqi" fontSize={12} color="#666" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const newFavoritedModelType = newlyFavoritedModel?.modelType?.toLowerCase();
  const showNewlyFavoritedModelTip = newFavoritedModelType === 'checkpoint';
  const showNewlyFavoritedLoraTip = newFavoritedModelType === 'lora';

  const [realLoraModels, hasHiddenLoras] = useMemo(() => {
    const realLoraModels = loraModels?.filter((lora) => !lora.isHiddenStrategy) || [];
    const hasHiddenLoras = loraModels?.some((lora) => lora.isHiddenStrategy) || false;

    return [realLoraModels, hasHiddenLoras];
  }, [loraModels]);

  return (
    <div id="generator-model-title">
      <h5 id="model-checkpoints-entry" className="text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
        {translate('model_title_checkpoint')}
        <span className="text-[#b2b2b2]">&ensp;Checkpoint</span>
      </h5>
      {showNormalCkptGide ? (
        <NormalGide currId={presetBaseModelId}>{modelEntry}</NormalGide>
      ) : showCheckpointAddedTip ? (
        <ModelAddedTip onClose={handleCloseModelAddedTip}>{modelEntry}</ModelAddedTip>
      ) : showSuggestionBaseModelTip ? (
        <SuggestionBaseModelTipWrapper>{modelEntry}</SuggestionBaseModelTipWrapper>
      ) : showNewlyFavoritedModelTip ? (
        <NewlyFavoritedModelTipWrapper>{modelEntry}</NewlyFavoritedModelTipWrapper>
      ) : (
        modelEntry
      )}
      <div className="-mx-4 my-3.5 border-t border-[#efefef]"></div>
      <div className="w-full" id="loraModelEntry">
        {showNewlyFavoritedLoraTip ? (
          <NewlyFavoritedModelTipWrapper>{loraTitle}</NewlyFavoritedModelTipWrapper>
        ) : (
          loraTitle
        )}
        {hasHiddenLoras && isPrimaryGenerator && (
          <div className="relative h-[50px] mt-2.5 group flex gap-3 items-center">
            <div className="flex-1" />
            <div className="absolute z-[1] inset-0 rounded overflow-hidden group-hover:right-6 transition-all">
              {generator.data.styleIcon ? (
                <img
                  src={convert2CompressedUrl(generator.data.styleIcon, 200)}
                  className="w-full h-[50px] object-cover"
                />
              ) : (
                <div className="w-full h-[50px] bg-[#222]" />
              )}
              <span className="absolute top-0 left-0 flex items-center px-2 h-5 bg-black/80 font-semibold rounded px-1 text-xs text-white">
                {translate('style_lora_title')}
              </span>
            </div>
            <button
              onClick={handleRemoveHiddenLoras}
              className="flex items-center ml-2 justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
            >
              <Icon className="icon-proshouqi" fontSize={12} color="#666" />
            </button>
          </div>
        )}
        {realLoraModels.map((lora, index, array) => {
          const item = renderLoraEntry(lora);
          const isLast = index === array.length - 1;

          if (isLast && showLoraAddedTip && item) {
            return (
              <ModelAddedTip key={lora?.versionId} onClose={handleCloseModelAddedTip}>
                {item}
              </ModelAddedTip>
            );
          }

          return <Fragment key={lora?.versionId}>{item}</Fragment>;
        })}
        {!realLoraModels.length && loraModels?.length < 5 && (
          <button
            data-trigger-window="model-loras"
            onClick={() => hanldeOpenLorasWindow()}
            className="w-full h-[50px] mt-2.5 bg-[#F4F6F8] hover:bg-[#f0f2f4] rounded flex px-4 items-center"
          >
            <img src={imgLoraEntry} alt="" />
            <span className="ml-3 text-xs text-[#7f7f7f]">{translate('model_addlora')}</span>
          </button>
        )}
      </div>
      {loraDetailOpened && openedLoraData && (
        <LoraDetailWindow key={openedLoraData?.versionId} data={openedLoraData} generatorId={generator.id} />
      )}
    </div>
  );
};
